import React from 'react';
import { motion } from 'framer-motion';
import { Play, Pause, RotateCcw, Volume2, Volume1, VolumeX } from 'lucide-react';

interface AudioControlsProps {
  isPlaying: boolean;
  onPlayPause: () => void;
  onReset: () => void;
  volume: number;
  onVolumeChange: (value: number) => void;
  isMuted: boolean;
  onMuteToggle: () => void;
}

export function AudioControls({
  isPlaying,
  onPlayPause,
  onReset,
  volume,
  onVolumeChange,
  isMuted,
  onMuteToggle
}: AudioControlsProps) {
  const VolumeIcon = isMuted ? VolumeX : volume > 0.5 ? Volume2 : Volume1;

  return (
    <div className="flex items-center gap-4">
      <div className="glass-panel p-2 cyber-border flex items-center gap-3">
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={onPlayPause}
          className="relative p-4 rounded-full bg-gradient-to-r from-blue-600 to-purple-600 
                     text-white transition-all duration-300 shadow-lg hover:shadow-xl 
                     neon-glow overflow-hidden group"
        >
          <motion.div
            className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent 
                       -translate-x-full group-hover:translate-x-full transition-transform duration-1000"
          />
          {isPlaying ? (
            <Pause className="w-6 h-6 relative z-10" />
          ) : (
            <Play className="w-6 h-6 relative z-10" />
          )}
          <motion.div
            initial={false}
            animate={isPlaying ? { scale: [1, 1.2, 1] } : { scale: 1 }}
            transition={{ duration: 1, repeat: isPlaying ? Infinity : 0 }}
            className="absolute inset-0 rounded-full bg-gradient-to-r from-blue-500/20 to-purple-500/20"
          />
        </motion.button>

        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={onReset}
          className="p-4 rounded-full bg-gray-800 text-gray-300 
                     hover:bg-gray-700 hover:text-white
                     transition-all duration-300 shadow-md hover:shadow-lg"
        >
          <RotateCcw className="w-6 h-6" />
        </motion.button>

        <div className="flex items-center gap-2 group relative">
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={onMuteToggle}
            className={`p-2 rounded-full transition-all duration-300
              ${isMuted 
                ? 'bg-red-900/20 text-red-400 hover:bg-red-900/30' 
                : 'bg-gray-800 text-gray-300 hover:bg-gray-700 hover:text-white'
              }`}
          >
            <VolumeIcon className="w-5 h-5" />
          </motion.button>

          <div className="relative">
            <input
              type="range"
              min="0"
              max="1"
              step="0.01"
              value={isMuted ? 0 : volume}
              onChange={(e) => onVolumeChange(parseFloat(e.target.value))}
              className="tech-slider w-24"
            />
            <motion.div
              initial={false}
              animate={{
                width: `${(isMuted ? 0 : volume) * 100}%`,
                opacity: isMuted ? 0.5 : 1
              }}
              className="absolute left-0 top-1/2 h-0.5 bg-gradient-to-r from-blue-500 to-purple-500 
                         -translate-y-1/2 pointer-events-none rounded-full"
            />
          </div>

          <div className="absolute -top-8 left-1/2 -translate-x-1/2 opacity-0 
                         group-hover:opacity-100 transition-opacity duration-200">
            <div className="px-2 py-1 rounded bg-gray-800 text-xs text-gray-300 whitespace-nowrap">
              Volume: {Math.round((isMuted ? 0 : volume) * 100)}%
            </div>
          </div>
        </div>

        {isPlaying && (
          <motion.div 
            className="flex items-center gap-1 px-3"
            animate={{
              opacity: [0.5, 1, 0.5]
            }}
            transition={{
              duration: 1.5,
              repeat: Infinity,
              ease: "easeInOut"
            }}
          >
            {[...Array(3)].map((_, i) => (
              <motion.div
                key={i}
                className="w-0.5 h-4 bg-blue-500 rounded-full"
                animate={{
                  height: ["16px", "24px", "16px"]
                }}
                transition={{
                  duration: 0.8,
                  repeat: Infinity,
                  delay: i * 0.2,
                  ease: "easeInOut"
                }}
              />
            ))}
          </motion.div>
        )}
      </div>
    </div>
  );
}